/**
 * Contacts Resolver
 * 
 * Fast lookup wrapper for phone enrichment.
 * Provides a simple interface for enrichment layer to query contacts.
 */

import type { ContactsLookupResult, ContactsBatchLookupResult } from './types';
import { WebexContactsManager } from './contactsManager';

/**
 * Simplified interface for contact lookups during enrichment
 * Wraps WebexContactsManager with a focused API
 */
export class ContactsResolver {
  private manager: WebexContactsManager;

  constructor(manager: WebexContactsManager) {
    this.manager = manager;
  }

  /**
   * Lookup a single phone number
   * Returns undefined if not found
   */
  resolve(phone: string): ContactsLookupResult | undefined {
    const result = this.manager.lookupByPhone(phone);
    return result;
  }

  /**
   * Resolve multiple phone numbers
   * Returns map of phone→contact for found entries
   */
  resolveMany(phones: string[]): Map<string, ContactsLookupResult> {
    const result = this.manager.lookupMany(phones);
    return result.found;
  }

  /**
   * Batch resolve with detailed results
   * Returns both found and not found phones
   */
  resolveBatch(phones: string[]): ContactsBatchLookupResult {
    return this.manager.lookupMany(phones);
  }

  /**
   * Check if resolver is ready to use
   */
  isReady(): boolean {
    const stats = this.manager.getStats();
    const expired = this.manager.isExpired();
    const hasContacts = stats.totalContacts > 0;
    const isInitialized = (this.manager as any).isInitialized;
    const ready = !expired && hasContacts && isInitialized;
    
    return ready;
  }

  /**
   * Get total contacts available
   */
  getTotalContacts(): number {
    const stats = this.manager.getStats();
    return stats.totalContacts;
  }

  /**
   * Trigger manual refresh of contacts
   */
  async refresh(): Promise<void> {
    await this.manager.refresh();
  }

  /**
   * Sync contacts from API while keeping the current cache visible (no clear).
   */
  async syncFromApi(): Promise<void> {
    await this.manager.syncFromApi();
  }
}
